import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Input, InputNumber, Select, Button, Space, DatePicker } from 'antd';
import { useConfigQuery } from '../../../queries/useConfig';
import { MoneyInput } from '../../../Componentes/MoneyInput';
import { InvoiceNumberInput } from '../../../Componentes/InvoiceNumberInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAdd, faMoneyBillWave, faTrash } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';

const { Option } = Select;

// ====== Helpers para documento CPF/CNPJ ======
const onlyDigits = (val = '') => {
    return val.replace(/\D/g, '').slice(0, 14); // máximo 14 dígitos
};

const formatDocumento = (digits = '') => {
    if (!digits) return '';

    // CPF até 11 dígitos -> 000.000.000-00
    if (digits.length <= 11) {
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return digits.replace(/(\d{3})(\d{1,3})/, '$1.$2');
        if (digits.length <= 9)
            return digits.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
        return digits.replace(
            /(\d{3})(\d{3})(\d{3})(\d{1,2})/,
            '$1.$2.$3-$4'
        );
    }

    // CNPJ 12–14 dígitos -> 00.000.000/0000-00
    if (digits.length <= 2) return digits;
    if (digits.length <= 5)
        return digits.replace(/(\d{2})(\d{1,3})/, '$1.$2');
    if (digits.length <= 8)
        return digits.replace(/(\d{2})(\d{3})(\d{1,3})/, '$1.$2.$3');
    if (digits.length <= 12)
        return digits.replace(
            /(\d{2})(\d{3})(\d{3})(\d{1,4})/,
            '$1.$2.$3/$4'
        );
    return digits.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{1,2})/,
        '$1.$2.$3/$4-$5'
    );
};

// ====== Helpers para pesos (BR visual, 4 casas com ponto no objeto) ======
const formatPesoBRFromStorage = (stored) => {
    if (stored === undefined || stored === null || stored === '') return '';

    const num = Number(stored);
    if (Number.isNaN(num)) return '';

    const fixed = num.toFixed(4); // "1234.5678"
    const [intPart, decPart] = fixed.split('.');

    const intWithSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // milhar

    return `${intWithSep},${decPart}`;
};

// converte texto digitado ("1.234,5678" ou "1234,5") -> "1234.5678"
const parsePesoBRToStorage = (raw) => {
    if (!raw) return '';

    // mantém apenas dígitos, vírgula e ponto
    let cleaned = raw.replace(/[^\d,\.]/g, '');

    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    const sepIndex = Math.max(lastComma, lastDot);

    let intPart;
    let decPart;

    if (sepIndex === -1) {
        intPart = cleaned;
        decPart = '';
    } else {
        intPart = cleaned.slice(0, sepIndex);
        decPart = cleaned.slice(sepIndex + 1);
    }

    intPart = intPart.replace(/\D/g, '');
    decPart = decPart.replace(/\D/g, '');

    if (!intPart && !decPart) return '';

    const joined = decPart ? `${intPart}.${decPart}` : intPart;
    const num = Number(joined);
    if (Number.isNaN(num)) return '';

    return num.toFixed(4); // "1234.5678"
};



const CobrancaForm = ({ fatura, setFatura, mostrarCobranca, setMostrarCobranca }) => {
    const { data: config, error } = useConfigQuery();


    const [parcelas, setParcelas] = useState({});



    const handleRemoveVol = (index) => {
        const list = parcelas.dup ? [...parcelas.dup] : [];
        list.splice(index, 1);
        updateParcelas({ dup: list });
    };

    const updateParcelas = (patch) => {
        console.log(patch);
        const newTransp = { ...parcelas, ...patch };
        setParcelas(newTransp);
        setFatura(prev => ({ ...prev, dup: newTransp }));
    };

    const handleParcelasChange = (index, field, newValue) => {
        const list = parcelas.dup ? [...parcelas.dup] : [];
        list[index] = { ...(list[index] || getDefaultParcelas()), [field]: newValue };
        updateParcelas({ dup: list });
    };


    const pad3 = (n) => String(n).padStart(3, '0');
    const hoje = new Date().toISOString().slice(0, 10);

    const getDefaultParcelas = (numeroParcela = 1) => ({
        nDup: pad3(numeroParcela), // "001", "002", ...
        dVenc: hoje,
        vDup: 0,
    });


    const addParcela = () => {
        const list = parcelas.dup ? [...parcelas.dup] : [];
        const proximoNumero = list.length + 1;
        list.push(getDefaultParcelas(proximoNumero));
        updateParcelas({ dup: list });
    };



    return (
        <div>
            <Card
                title="Fatura"
                extra={
                    <Button
                        onClick={() => setMostrarCobranca((v) => !v)}
                        style={{
                            backgroundColor: "#000",
                            color: "#fff",
                            border: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                        }}
                        icon={<FontAwesomeIcon icon={faMoneyBillWave} />}
                    >
                        Cobrança
                    </Button>
                }
            >
                {mostrarCobranca && (
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                    gap: 12,
                                }}
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label>Número da fatura</label>
                                    <InvoiceNumberInput
                                        className="input-estilizado"
                                        value={fatura.nFat}
                                        onChange={(v) => setFatura((prev) => ({ ...prev, nFat: v }))}
                                    />
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label>Valor original</label>
                                    <MoneyInput
                                        className="input-estilizado"
                                        value={fatura.vOrig}
                                        onChange={(v) => setFatura((prev) => ({ ...prev, vOrig: v }))}
                                    />
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label>Valor do desconto</label>
                                    <MoneyInput
                                        className="input-estilizado"
                                        value={fatura.vDesc}
                                        onChange={(v) => setFatura((prev) => ({ ...prev, vDesc: v }))}
                                    />
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label>Valor líquido</label>
                                    <MoneyInput
                                        className="input-estilizado"
                                        value={fatura.vLiq}
                                        onChange={(v) => setFatura((prev) => ({ ...prev, vLiq: v }))}
                                    />
                                </div>
                            </div>

                        </Col>
                    </Row>
                )}
            </Card>
           {mostrarCobranca && (  <Card
                title="Parcelas"
                extra={

                    <Button size="small" onClick={addParcela} style={{
                        backgroundColor: "#000",
                        color: "#fff",
                        border: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent:"center",
                        height:"32px"
                    }}>
                        Adicionar parcela
                    </Button>
                }
            >

                         {parcelas.dup?.map((vol, vIndex) => (
                                    <Card
                                        key={vIndex}
                                        size="small"
                                        type="inner"
                                        title={`Volume #${vIndex + 1}`}
                                        extra={
                                            <Button size="small" danger onClick={() => handleRemoveVol(vIndex)}>
                                                Remover
                                            </Button>
                                        }
                                        style={{ marginBottom: 12 }}
                                    >
                                        <Row gutter={16}>
                                            <Col xs={24} md={8}>
                                                <label>Parcela</label>
                                                <Input
                                                    style={{ width: '100%' }}
                                                    value={vol.nDup}
                                                    onChange={(e) => handleParcelasChange(vIndex, 'nDup', e.target.value)}
                                                />
                                            </Col>

                                            <Col xs={24} md={8}>
                                                <label>Valor</label>
                                                <MoneyInput
                                                    style={{ width: '100%' }}
                                                    className="input-estilizado"
                                                    value={vol.vDup}
                                                    onChange={(val) => handleParcelasChange(vIndex, 'vDup', val)}
                                                />
                                            </Col>

                                            <Col xs={24} md={8}>
                                                <label>Vencimento</label>
                                                <Input
                                                    type="date"
                                                    style={{ width: '100%' }}
                                                    value={vol.dVenc}
                                                    onChange={(e) => handleParcelasChange(vIndex, 'dVenc', e.target.value)}
                                                />
                                            </Col>


                                        </Row>
                                    </Card>
                                ))}
                {mostrarCobranca && (
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            {/*<div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 1fr 1fr",
                                    gap: 12,
                                }}
                            >
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label>Quantidade de parcelas</label>
                                    <InputNumber
                                        className="input-estilizado"
                                        style={{
                                            width: '173px',
                                            height: '44px',
                                            border: '1px solid #000',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                        value={nFat}
                                        onChange={setnFat}
                                        controls={false}
                                        inputStyle={{
                                            textAlign: 'center',
                                            height: '44px',
                                            lineHeight: '44px'
                                        }}
                                    />
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label>Intervalo</label>
                                    <MoneyInput
                                        className="input-estilizado"
                                        value={vOrig}
                                        onChange={setvOrig}
                                    />
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label>Primeiro vencimento</label>
                                    <MoneyInput
                                        className="input-estilizado"
                                        value={vDesc}
                                        onChange={setvDesc}
                                    />
                                </div>


                            </div>
                            <br />*/}

                        </Col>
                    </Row>
                )}
            </Card>)}
        </div>
    );
};

export default CobrancaForm;
